import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Label } from '@ui/components/ui/label'
import { Button } from '@ui/components/ui/button'
import { useDataStore } from '@/lib/machine/use-data-store'
import { trimCommas } from '@/lib/utils'
import { LoaderCircleIcon } from 'lucide-react'
import { Input } from '../installation/stages/1-setup/setup-form-components'
import { MachineSystemData } from '@uv/machine'

const CALIBRATION_FIELDS = [
  'rpm_factor',
  'slip_factor',
  'mains_voltage',
  'uv_to_watt',
  'current_factor'
] as const

type CalibrationField = (typeof CALIBRATION_FIELDS)[number]

type FormState = Record<CalibrationField, string>

const toFormState = (calibration: MachineSystemData['CONFIGURATION'] | null): FormState => ({
  rpm_factor: calibration?.rpm_factor?.toString() ?? '',
  slip_factor: calibration?.slip_factor?.toString() ?? '',
  mains_voltage: calibration?.mains_voltage?.toString() ?? '',
  uv_to_watt: calibration?.uv_to_watt?.toString() ?? '',
  current_factor: calibration?.current_factor?.toString() ?? ''
})

const parseFormToData = (form: FormState): Partial<MachineSystemData['CONFIGURATION']> => {
  const data: Partial<MachineSystemData['CONFIGURATION']> = {}

  for (const field of CALIBRATION_FIELDS) {
    const raw = trimCommas(form[field])
    const parsed = parseFloat(raw)
    if (!isNaN(parsed)) {
      data[field] = parsed
    }
  }

  return data
}

type Props = {
  onClose: () => void
}

export const MachineCalibrationForm = ({ onClose }: Props) => {
  const { t } = useTranslation()
  const machineCalibration = useDataStore(s => s.machineCalibration)
  const sendMachineAction = useDataStore(s => s.sendMachineAction)

  const [form, setForm] = useState<FormState>(() => toFormState(machineCalibration))
  const availableFields = machineCalibration
    ? CALIBRATION_FIELDS.filter(field => machineCalibration[field] !== undefined)
    : []

  // Fetch current calibration from machine on mount
  useEffect(() => {
    sendMachineAction({ in: 'machine_configuration', data: {} })
  }, [sendMachineAction])

  // Sync form when machine calibration is received
  useEffect(() => {
    if (machineCalibration) {
      setForm(toFormState(machineCalibration))
    }
  }, [machineCalibration])

  const handleFieldChange = (field: CalibrationField, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleSave = () => {
    sendMachineAction({
      in: 'machine_configuration',
      data: parseFormToData(form)
    })

    onClose()
  }

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-2xl font-semibold">{t('settings.machine.calibration-title')}</h2>

      {machineCalibration ? (
        <div className="grid grid-cols-2 gap-x-16 gap-y-4">
          {availableFields.map(field => (
            <div key={field} className="flex items-center justify-between gap-4">
              <Label htmlFor={`cal-${field}`} className="text-md">
                {t(`settings.machine.calibration-${field}`)}
              </Label>
              <Input
                id={`cal-${field}`}
                type="number"
                step="any"
                placeholder="0"
                className="w-32 text-right"
                value={form[field]}
                onChange={e => handleFieldChange(field, e.target.value)}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="flex justify-center pt-10 pb-12">
          <LoaderCircleIcon className="animate-spin h-10 w-10" />
        </div>
      )}

      <div className="flex self-end gap-4 mt-8">
        <Button
          variant="outline"
          className="w-1/2 p-8 text-md font-bold rounded-xl"
          onClick={onClose}
        >
          {t('settings.machine.calibration-cancel')}
        </Button>
        {machineCalibration && (
          <Button className="w-1/2 p-8 text-md font-bold rounded-xl" onClick={handleSave}>
            {t('settings.machine.calibration-save')}
          </Button>
        )}
      </div>
    </div>
  )
}
